export const buildScriptableWidget = (apiBase, key) => {
  const host = apiBase.replace(/^https?:\/\//, "");
  return `// Thrifty Curator — Home Screen Widget (Scriptable) · v3
// Best as a LARGE widget. Tap the Messages card → opens Messages; tap anywhere else → opens the app site.
const APP_HOST = "${host}";
const API = "https://" + APP_HOST + "/api/widget/glance?key=${key}";
const SITE_URL = "https://" + APP_HOST + "/admin";
const MESSAGES_URL = "https://" + APP_HOST + "/admin?openMessages=true";
const NOTIFICATIONS_URL = "https://" + APP_HOST + "/admin?openNotifications=true";
// Team members to hide (test accounts etc.)
const HIDE_TEAM = ["Matthew Guzman"];
// Widget "Parameter" (long-press widget → Edit Widget → Parameter):
//   (empty) = everything · "messages" · "notifications" · "team" · "payroll" = that section only
// Tip: put a "messages" copy under the main widget in a Smart Stack and swipe between them.
const MODE = (args.widgetParameter || "").trim().toLowerCase();

// ---- Theme (matches the app) ----
const C = {
  bg1: "#1A1A2E", bg2: "#16213E", bg3: "#0F1626",
  cyan: "#00D4FF", purple: "#8B5CF6", pink: "#FF1493", green: "#10B981", amber: "#FFB800",
  text: "#FFFFFF", dim: "#A7ADC2", faint: "#636A85", card: "#FFFFFF"
};
const col = (hex, a) => (a == null ? new Color(hex) : new Color(hex, a));
const fam = config.widgetFamily || "large";
const isLarge = fam === "large" || fam === "extraLarge";
const isSmall = fam === "small";

let data = null;
let keyRejected = false;
try { data = await new Request(API).loadJSON(); } catch (e) {}
if (data && data.detail) { keyRejected = true; data = null; }

const w = new ListWidget();
const bg = new LinearGradient();
bg.colors = [col(C.bg1), col(C.bg2), col(C.bg3)];
bg.locations = [0, 0.5, 1];
bg.startPoint = new Point(0, 0);
bg.endPoint = new Point(1, 1);
w.backgroundGradient = bg;
w.setPadding(10, 12, 8, 12);
w.url = SITE_URL;
// Ask iOS to refresh in 5 min. iOS treats this as the earliest allowed time and budgets
// roughly 40-70 refreshes per widget per day, so in practice expect every 5-15 min.
const REFRESH_MINUTES = 5;
w.refreshAfterDate = new Date(Date.now() + REFRESH_MINUTES * 60 * 1000);

// ---- helpers ----
const ago = (iso) => {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "now";
  if (m < 60) return m + "m";
  const h = Math.floor(m / 60);
  return h < 24 ? h + "h" : Math.floor(h / 24) + "d";
};
const exact = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const mmdd = (iso) => (iso ? iso.slice(5, 10).replace("-", "/") : "");
const firstName = (n) => (n || "").split(" ")[0];

function text(stack, str, size, color, opts = {}) {
  const t = stack.addText(String(str));
  t.font = opts.weight === "heavy" ? Font.heavySystemFont(size)
        : opts.weight === "bold" ? Font.boldSystemFont(size)
        : opts.weight === "semi" ? Font.semiboldSystemFont(size)
        : Font.systemFont(size);
  t.textColor = col(color, opts.alpha);
  t.lineLimit = opts.lines == null ? 1 : opts.lines; // 0 = unlimited
  if (opts.scale) t.minimumScaleFactor = opts.scale;
  return t;
}
function icon(stack, name, size, color) {
  const sym = SFSymbol.named(name);
  sym.applyFont(Font.semiboldSystemFont(size));
  const im = stack.addImage(sym.image);
  im.imageSize = new Size(size, size);
  im.tintColor = col(color);
  return im;
}
function divider(stack, color) {
  const d = stack.addStack();
  d.size = new Size(0, 1);
  d.backgroundColor = col(color || C.card, 0.08);
  d.addSpacer();
}
// Card: glassy rounded panel with a thin colored accent strip at the top
function card(parent, accent, url) {
  const outer = parent.addStack();
  outer.layoutVertically();
  outer.cornerRadius = 14;
  outer.backgroundColor = col(C.card, 0.07);
  outer.borderWidth = 1;
  outer.borderColor = col(C.card, 0.10);
  if (url) outer.url = url;
  const strip = outer.addStack();
  strip.size = new Size(0, 3);
  const sg = new LinearGradient();
  sg.colors = [col(accent), col(accent, 0.15)];
  sg.locations = [0, 1];
  sg.startPoint = new Point(0, 0);
  sg.endPoint = new Point(1, 0);
  strip.backgroundGradient = sg;
  strip.addSpacer();
  const body = outer.addStack();
  body.layoutVertically();
  body.setPadding(7, 10, 8, 10);
  return { outer, body };
}
function cardTitle(body, sym, color, title, rightText) {
  const r = body.addStack();
  r.centerAlignContent();
  icon(r, sym, 10, color);
  r.addSpacer(5);
  text(r, title.toUpperCase(), 9, color, { weight: "bold" });
  r.addSpacer();
  if (rightText) text(r, rightText, 9, C.faint);
}

// ---- header ----
const hd = w.addStack();
hd.centerAlignContent();
const badge = hd.addStack();
badge.setPadding(2, 6, 2, 6);
badge.cornerRadius = 6;
const bgG = new LinearGradient();
bgG.colors = [col(C.cyan), col(C.purple), col(C.pink)];
bgG.locations = [0, 0.5, 1];
badge.backgroundGradient = bgG;
text(badge, "TC", 10, "#0a0a14", { weight: "heavy" });
hd.addSpacer(7);
text(hd, isSmall ? "Thrifty" : "Thrifty Curator", 13, C.text, { weight: "semi" });
hd.addSpacer();
text(hd, "Updated " + new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }), 9, C.faint);
w.addSpacer(9);

if (!data) {
  const { body } = card(w, C.pink);
  cardTitle(body, keyRejected ? "key.slash" : "wifi.exclamationmark", C.pink, keyRejected ? "Widget key rejected" : "Offline");
  body.addSpacer(4);
  text(body, keyRejected ? "This script's key is no longer valid. Open Admin → Operations → Widgets and re-copy the script." : "Couldn't reach the app. Tap to open.", 11, C.dim, { lines: 3 });
} else {
  const m = data.messages || {};
  const recent = m.recent || [];
  const team = (data.team || []).filter((t) => !HIDE_TEAM.includes(t.name));
  const p = data.payroll || {};
  const unread = m.unread_count || 0;
  const only = (k) => MODE === k;
  const showMsgs = !MODE || only("messages");
  const showTeam = !isSmall && (!MODE || only("team"));
  const showPay = !isSmall && (!MODE || only("payroll"));
  const showNotifs = isLarge && (!MODE || only("notifications"));
  const n = data.notifications || {};
  const notifs = n.recent || [];
  const msgCount = only("messages") ? (isLarge ? 7 : 3) : isSmall ? 1 : isLarge ? 3 : 1;
  const notifCount = only("notifications") ? 9 : 3;
  const notifIcon = (t) => /clock_in/.test(t) ? "arrow.right.circle.fill" : /clock_out/.test(t) ? "arrow.left.circle.fill"
    : /w9|form|agreement|approval/.test(t) ? "doc.text.fill" : /application|applicant/.test(t) ? "person.badge.plus"
    : /payment|payroll/.test(t) ? "creditcard.fill" : /order|pull/.test(t) ? "shippingbox.fill" : "bell.fill";
  const teamCount = only("team") ? 12 : isLarge ? 6 : 2;
  const who = (msg) => (msg.mine ? "You" : msg.from) + (msg.is_admin && msg.conversation ? " → " + msg.conversation : "");

  // ===== MESSAGES (tap → Messages) =====
  if (showMsgs) {
    const { body } = card(w, C.green, MESSAGES_URL);
    cardTitle(body, "message.fill", C.green, only("messages") ? "Recent conversation" : "Messages", unread ? unread + " unread" : "All caught up");
    if (recent.length === 0) { body.addSpacer(4); text(body, "No messages yet", 11, C.faint); }
    recent.slice(0, msgCount).forEach((msg, i) => {
      body.addSpacer(i === 0 ? 5 : 4);
      const row = body.addStack();
      row.centerAlignContent();
      const dot = row.addStack();
      dot.size = new Size(6, 6);
      dot.cornerRadius = 3;
      dot.backgroundColor = msg.is_admin ? col(C.purple) : msg.read ? col(C.card, 0.18) : col(C.green);
      row.addSpacer(6);
      text(row, who(msg), 11, msg.is_admin ? C.purple : C.text, { weight: msg.read ? "semi" : "bold" });
      row.addSpacer();
      text(row, ago(msg.sent_at), 9, C.faint);
      const pv = body.addStack();
      pv.addSpacer(12);
      // Newest message in full; older ones trimmed to keep everything on screen
      const full = i === 0 || only("messages");
      text(pv, (msg.text || msg.preview || "").trim(), 10.5, msg.is_admin ? C.dim : msg.read ? C.dim : C.text, { lines: full ? (i === 0 ? 3 : 2) : 1 });
    });
    if (only("messages")) body.addSpacer();
  }

  // ===== NOTIFICATIONS (bell: clock in/out, forms, applications…) — tap → bell =====
  if (showNotifs) {
    if (showMsgs) w.addSpacer(8);
    const { body } = card(w, C.amber, NOTIFICATIONS_URL);
    cardTitle(body, "bell.fill", C.amber, "Notifications", n.unread_count ? n.unread_count + " new" : "Nothing new");
    if (notifs.length === 0) { body.addSpacer(4); text(body, "No recent activity", 11, C.faint); }
    notifs.slice(0, notifCount).forEach((nt, i) => {
      body.addSpacer(i === 0 ? 5 : 3);
      const row = body.addStack();
      row.centerAlignContent();
      icon(row, notifIcon(nt.type || ""), 10, nt.read ? C.faint : C.amber);
      row.addSpacer(6);
      const msgTxt = (nt.message || "").trim();
      const nameIn = nt.employee_name && msgTxt.toLowerCase().includes(nt.employee_name.toLowerCase());
      const txt = (nt.employee_name && !nameIn ? nt.employee_name + ": " : "") + msgTxt;
      text(row, txt, 10.5, nt.read ? C.dim : C.text, { weight: nt.read ? "regular" : "semi", lines: 2, scale: 0.85 });
      row.addSpacer(6);
      text(row, ago(nt.created_at), 9, C.faint);
    });
    if (only("notifications")) body.addSpacer();
  }

  if (showTeam) {
    if (showMsgs || showNotifs) w.addSpacer(8);
    // ===== TEAM (clocked in + last shift) =====
    {
      const onNow = team.filter((t) => t.clocked_in).length;
      const { body } = card(w, C.cyan);
      cardTitle(body, "person.2.fill", C.cyan, "Team", onNow ? onNow + " on the clock" : "Nobody clocked in");
      const show = teamCount;
      team.slice(0, show).forEach((t, i) => {
        body.addSpacer(i === 0 ? 5 : 3);
        const row = body.addStack();
        row.centerAlignContent();
        const dot = row.addStack();
        dot.size = new Size(6, 6);
        dot.cornerRadius = 3;
        dot.backgroundColor = t.clocked_in ? col(C.green) : col(C.card, 0.18);
        row.addSpacer(6);
        text(row, t.name, 11, C.text, { weight: "semi" });
        row.addSpacer();
        if (t.clocked_in) {
          text(row, "on the clock · " + ago(t.clock_in_time), 10, C.green, { weight: "semi" });
        } else if (t.last_shift_date) {
          text(row, "last shift " + (+Number(t.last_shift_hours || 0).toFixed(2)) + "h · " + mmdd(t.last_shift_date), 10, C.dim);
        } else {
          text(row, "no shifts yet", 10, C.faint);
        }
        if (only("team")) {
          const pe = (p.by_employee || []).find((e) => e.name === t.name);
          const sub = body.addStack();
          sub.addSpacer(12);
          text(sub, pe ? "this period " + (+Number(pe.hours || 0).toFixed(2)) + "h · " + exact(pe.amount) : "no hours this period", 9.5, pe ? C.cyan : C.faint);
        }
      });
      if (team.length > show) { body.addSpacer(3); text(body, "+" + (team.length - show) + " more", 9, C.faint); }
    }

  }
  if (showPay) {
    if (showMsgs || showNotifs || showTeam) w.addSpacer(8);
    // ===== PAYROLL (exact estimate bottom-right) =====
    {
      const { body } = card(w, C.purple);
      cardTitle(body, "dollarsign.circle.fill", C.purple, "Payroll · this period", mmdd(p.period_start) + " – " + mmdd(p.period_end));
      body.addSpacer(5);
      const row = body.addStack();
      row.bottomAlignContent();
      const left = row.addStack();
      left.layoutVertically();
      text(left, (+Number(p.hours || 0).toFixed(2)) + " hrs logged", 11, C.text, { weight: "semi" });
      text(left, p.outstanding_amount > 0 ? exact(p.outstanding_amount) + " still unpaid" : "Nothing unpaid from past periods", 10, p.outstanding_amount > 0 ? C.amber : C.faint);
      row.addSpacer();
      const right = row.addStack();
      right.layoutVertically();
      right.bottomAlignContent();
      const lbl = right.addStack(); lbl.addSpacer(); text(lbl, "ESTIMATED", 8, C.purple, { weight: "bold" });
      const amt = right.addStack(); amt.addSpacer(); text(amt, exact(p.amount), 20, C.text, { weight: "heavy", scale: 0.6 });
      if (only("payroll")) {
        const emps = (p.by_employee || []).filter((e) => !HIDE_TEAM.includes(e.name));
        if (emps.length) { body.addSpacer(6); divider(body); }
        emps.slice(0, 10).forEach((e) => {
          body.addSpacer(4);
          const r = body.addStack();
          r.centerAlignContent();
          text(r, e.name, 11, C.text, { weight: "semi" });
          r.addSpacer();
          text(r, (+Number(e.hours || 0).toFixed(2)) + "h", 10, C.dim);
          r.addSpacer(10);
          text(r, exact(e.amount), 11, C.purple, { weight: "bold" });
        });
        body.addSpacer();
      }
    }
  }
}
w.addSpacer();

if (config.runsInWidget) Script.setWidget(w);
else await (isLarge ? w.presentLarge() : isSmall ? w.presentSmall() : w.presentMedium());
Script.complete();
`;
};
